import { TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import {
  DatasourceContentType,
  FlowContentType,
  IContentType,
  PublishedConnectionContentType,
  WorkbookContentType,
} from '~src/spacesPlatform/models/ContentTypes';
import { ContentUrlForItem } from '~src/spacesPlatform/services/ContentUrl';

import { Messages } from '../../../codegen/Localize';
import { TaskDetailsSpaceStyles } from './TaskDetailsSpaceStyles';
import { ITaskDetailsViewModel } from './types/ITaskDetails';

export const TaskMetadataSectionTestIds = {
  Section: 'task-metadata-section',
  ContentLink: 'task-metadata-content-link',
  fieldValue: (key: string) => `task-metadata-value-${key}`,
};

export interface TaskMetadataSectionProps {
  readonly taskDetails: ITaskDetailsViewModel;
}

// Backend `contentType` values (S02 contract) mapped to the IContentType used by ContentUrlForItem.
const CONTENT_TYPE_BY_NAME: { [key: string]: IContentType } = {
  Workbook: WorkbookContentType,
  Datasource: DatasourceContentType,
  Flow: FlowContentType,
  VirtualConnection: PublishedConnectionContentType,
};

/**
 * Resolves the content overview URL for the task's associated content, or null if unresolvable.
 * Uses the numeric routing `contentId` (unified connection ID for datasources, DB primary key
 * otherwise) — the same identifier the Jobs-page Content column uses. The content LUID cannot be
 * resolved by the content page routes.
 */
function contentHref(contentId: string | null, contentType: string | null): string | null {
  if (!contentId || !contentType) {
    return null;
  }
  const itemType = CONTENT_TYPE_BY_NAME[contentType];
  if (!itemType) {
    return null;
  }
  const url = ContentUrlForItem({ id: contentId }, itemType);
  return url ? url : null;
}

/**
 * Task metadata grid (S05-b, PRD 2.2.8). Renders the task data fields as label/value rows.
 * Null values render as a grayed dash (PRD null-handling contract); the content name renders
 * as a link to its overview page when the content is still resolvable.
 */
export const TaskMetadataSection: React.FunctionComponent<TaskMetadataSectionProps> = props => {
  const { taskDetails } = props;

  const renderValue = (key: string, value: string | null): JSX.Element => {
    const isEmpty = value === null || value === undefined || value === '';
    return (
      <dd
        className={isEmpty ? TaskDetailsSpaceStyles.MetadataValueEmpty : TaskDetailsSpaceStyles.MetadataValue}
        data-tb-test-id={TaskMetadataSectionTestIds.fieldValue(key)}
      >
        {isEmpty ? Messages.taskDetailsValueNone() : value}
      </dd>
    );
  };

  const renderRow = (key: string, label: string, value: string | null): JSX.Element => (
    <div className={TaskDetailsSpaceStyles.MetadataRow} key={key}>
      <dt className={TaskDetailsSpaceStyles.MetadataLabel}>{label}</dt>
      {renderValue(key, value)}
    </div>
  );

  const renderNextRunRow = (): JSX.Element => {
    const displayValue = taskDetails.refreshExtractTriggered
      ? Messages.subscriptionOnExtractRefreshNextUpdate()
      : taskDetails.nextRunAt;
    return (
      <div className={TaskDetailsSpaceStyles.MetadataRow} key="nextRunAt">
        <dt className={TaskDetailsSpaceStyles.MetadataLabel}>{Messages.taskDetailsFieldNextRun()}</dt>
        {renderValue('nextRunAt', displayValue)}
      </div>
    );
  };

  const renderContentRow = (): JSX.Element => {
    const href = contentHref(taskDetails.contentId, taskDetails.contentType);
    const hasName = !!taskDetails.contentName;
    return (
      <div className={TaskDetailsSpaceStyles.MetadataRow} key="content">
        <dt className={TaskDetailsSpaceStyles.MetadataLabel}>{Messages.taskDetailsFieldContent()}</dt>
        {hasName && href
          ? <dd className={TaskDetailsSpaceStyles.MetadataValue}>
              <TextLinkWidget href={href} testId={TaskMetadataSectionTestIds.ContentLink}>
                {taskDetails.contentName}
              </TextLinkWidget>
            </dd>
          : renderValue('content', taskDetails.contentName)}
      </div>
    );
  };

  // Fields are grouped into three vertical columns laid out horizontally (Figma):
  //  1. Content identity, 2. Run timing aggregates, 3. Task config / people.
  return (
    <div className={TaskDetailsSpaceStyles.MetadataColumns} data-tb-test-id={TaskMetadataSectionTestIds.Section}>
      <dl className={TaskDetailsSpaceStyles.MetadataGrid}>
        {renderContentRow()}
        {renderRow('contentOwner', Messages.taskDetailsFieldContentOwner(), taskDetails.contentOwnerEmail)}
        {renderRow('project', Messages.taskDetailsFieldProject(), taskDetails.project)}
        {renderRow('site', Messages.taskDetailsFieldSite(), taskDetails.site)}
      </dl>
      <dl className={TaskDetailsSpaceStyles.MetadataGrid}>
        {renderRow('lastSuccessfulRunAt', Messages.taskDetailsFieldLastSuccessfulRun(), taskDetails.lastSuccessfulRunAt)}
        {renderNextRunRow()}
        {renderRow('avgRunTime', Messages.taskDetailsFieldAvgRunTime(), taskDetails.avgRunTime)}
        {renderRow('avgQueueTime', Messages.taskDetailsFieldAvgQueueTime(), taskDetails.avgQueueTime)}
      </dl>
      <dl className={TaskDetailsSpaceStyles.MetadataGrid}>
        {renderRow('taskType', Messages.taskDetailsFieldTaskType(), taskDetails.taskType)}
        {renderRow('schedule', Messages.taskDetailsFieldSchedule(), taskDetails.schedule)}
        {renderRow('priority', Messages.taskDetailsFieldPriority(), taskDetails.priority)}
        {renderRow('creator', Messages.taskDetailsFieldCreator(), taskDetails.creatorEmail)}
      </dl>
    </div>
  );
};
